import type { InboxConversationDetail } from '@sbc/domain-models/gateway';
import { Plus, X } from 'lucide-react';
import { type KeyboardEvent, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAddInboxTag, useInboxTags, useRemoveInboxTag } from '@/lib/api/inbox';

import { tagPicks, tagToAdd } from './inbox-tags';

export function ConversationTags({ conversation }: { conversation: InboxConversationDetail }) {
  const all = useInboxTags();
  const add = useAddInboxTag(conversation.id);
  const remove = useRemoveInboxTag(conversation.id);
  const [typed, setTyped] = useState('');
  const picks = tagPicks(all.data?.tags ?? [], conversation.tags, typed);
  const busy = add.isPending || remove.isPending;
  const error = add.error ?? remove.error;

  const addTag = (name: string) => {
    add.mutate({ name }, { onSuccess: () => setTyped('') });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const name = tagToAdd(picks, typed);
      if (name && !busy) {
        addTag(name);
      }
    }
    if (event.key === 'Escape') {
      setTyped('');
      event.currentTarget.blur();
    }
  };

  return (
    <div className="space-y-2">
      {conversation.tags.length > 0 ? (
        <ul aria-label="Tags" className="flex flex-wrap gap-1">
          {conversation.tags.map((tag) => (
            <li key={tag.id}>
              <Badge variant="secondary" className="pr-1">
                {tag.name}
                <button
                  type="button"
                  aria-label={`Remove tag ${tag.name}`}
                  className="rounded-sm opacity-60 hover:opacity-100 disabled:opacity-30"
                  disabled={busy}
                  onClick={() => remove.mutate(tag.id)}
                >
                  <X className="size-3" />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-[var(--color-muted-foreground)]">No tags yet.</p>
      )}
      <Input
        value={typed}
        onChange={(event) => setTyped(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Add a tag…"
        aria-label="Add a tag"
        className="h-8 text-sm"
        disabled={busy}
      />
      {typed.trim().length > 0 && (picks.suggestions.length > 0 || picks.newName) && (
        <div aria-label="Tag suggestions" className="flex flex-wrap gap-1">
          {picks.suggestions.map((tag) => (
            <Button
              key={tag.id}
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              disabled={busy}
              onClick={() => addTag(tag.name)}
            >
              {tag.name}
            </Button>
          ))}
          {picks.newName && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              disabled={busy}
              onClick={() => addTag(picks.newName ?? '')}
            >
              <Plus className="size-3" />
              Create “{picks.newName}”
            </Button>
          )}
        </div>
      )}
      {error && <p className="text-sm text-[var(--color-destructive)]">{error.message}</p>}
    </div>
  );
}
